import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Scissors,
  Leaf,
  Sprout,
  TreeDeciduous,
  Recycle,
  Flower2,
  Check,
  Phone,
  ArrowRight,
} from "lucide-react";

const ServicesPage = () => {
  const services = [
    {
      id: "lawn-mowing",
      icon: Scissors,
      title: "Lawn Mowing",
      desc: "Professional mowing services to keep your lawn looking pristine all year round.",
      image: "https://images.unsplash.com/photo-1630311853242-edd0c07766c0?auto=format&fit=crop&w=800&q=80",
      benefits: [
        "Weekly and bi-weekly scheduling options",
        "Precision cutting at optimal height",
        "Clean edges and trimming included",
        "Grass clipping removal available",
        "Consistent patterns for a manicured look",
      ],
      process: [
        "Assessment of lawn condition and type",
        "Setting optimal mowing height",
        "Precise mowing with professional equipment",
        "Detailed edging along walkways and beds",
        "Debris cleanup and quality check",
      ],
    },
    {
      id: "lawn-maintenance",
      icon: Leaf,
      title: "Lawn Maintenance",
      desc: "Complete lawn care including edging, trimming, and seasonal maintenance for a healthy yard.",
      image: "https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=800&q=80",
      benefits: [
        "Comprehensive care packages",
        "Regular edging and trimming",
        "Leaf and debris removal",
        "Seasonal adjustments",
        "Proactive problem detection",
      ],
      process: [
        "Initial lawn health assessment",
        "Customized maintenance plan",
        "Regular scheduled visits",
        "Ongoing monitoring and adjustments",
        "Seasonal service modifications",
      ],
    },
    {
      id: "weed-control",
      icon: Sprout,
      title: "Weed Control",
      desc: "Effective weed prevention and elimination to protect your lawn's health and appearance.",
      image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=800&q=80",
      benefits: [
        "Pre-emergent weed prevention",
        "Post-emergent weed treatment",
        "Targeted spot treatments",
        "Safe for pets and children",
        "Long-lasting results",
      ],
      process: [
        "Weed identification and assessment",
        "Selection of appropriate treatments",
        "Careful application of products",
        "Follow-up monitoring",
        "Preventive maintenance plan",
      ],
    },
    {
      id: "fertilization",
      icon: TreeDeciduous,
      title: "Fertilization",
      desc: "Custom fertilization programs designed to promote lush, green, healthy grass growth.",
      image: "https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e?auto=format&fit=crop&w=800&q=80",
      benefits: [
        "Soil testing and analysis",
        "Custom nutrient programs",
        "Slow-release formulations",
        "Seasonal applications",
        "Visible results in weeks",
      ],
      process: [
        "Soil testing and analysis",
        "Custom program development",
        "Scheduled fertilizer applications",
        "Progress monitoring",
        "Program adjustments as needed",
      ],
    },
    {
      id: "yard-cleanup",
      icon: Recycle,
      title: "Yard Cleanup",
      desc: "Seasonal and one-time cleanups to remove debris, leaves, and restore your yard's beauty.",
      image: "https://images.unsplash.com/photo-1558171813-4c088753af8f?auto=format&fit=crop&w=800&q=80",
      benefits: [
        "Spring and fall cleanup services",
        "Leaf removal and disposal",
        "Debris and branch removal",
        "Bed cleanup and refreshing",
        "Storm damage cleanup",
      ],
      process: [
        "Property walkthrough assessment",
        "Removal of leaves and debris",
        "Branch and twig cleanup",
        "Bed and border tidying",
        "Final inspection and haul-away",
      ],
    },
    {
      id: "landscaping",
      icon: Flower2,
      title: "Landscaping Maintenance",
      desc: "Professional maintenance for your landscape beds, shrubs, and garden areas.",
      image: "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?auto=format&fit=crop&w=800&q=80",
      benefits: [
        "Shrub and hedge trimming",
        "Mulch installation",
        "Bed weeding and maintenance",
        "Seasonal color installation",
        "Plant health monitoring",
      ],
      process: [
        "Landscape assessment",
        "Customized maintenance plan",
        "Regular pruning and trimming",
        "Mulch and bed maintenance",
        "Seasonal updates and planting",
      ],
    },
  ];

  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-forest-950 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-emerald-400 font-accent font-medium uppercase tracking-wider text-sm">Our Services</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mt-3 mb-6">
              Professional Lawn Care Services
            </h1>
            <p className="text-emerald-100/80 text-lg">
              From regular maintenance to specialized treatments, we offer comprehensive solutions to keep your lawn healthy, beautiful, and thriving.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
          {services.map((service, index) => (
            <div 
              key={service.id}
              id={service.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              data-testid={`service-${service.id}`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <service.icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-heading font-bold text-forest-950">{service.title}</h2>
                </div>
                
                <p className="text-gray-600 text-lg mb-8">{service.desc}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h3 className="font-heading font-semibold text-forest-950 mb-4">Benefits</h3>
                    <ul className="space-y-3">
                      {service.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-heading font-semibold text-forest-950 mb-4">Our Process</h3>
                    <ol className="space-y-3">
                      {service.process.map((step, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-sm font-semibold flex-shrink-0">
                            {i + 1}
                          </span>
                          <span className="text-gray-600">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
                
                <Link to="/contact">
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-4"
                    data-testid={`cta-${service.id}`}
                  >
                    Get a Quote for {service.title}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
              
              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <img 
                  src={service.image}
                  alt={service.title}
                  className="rounded-2xl shadow-lg w-full h-80 lg:h-96 object-cover"
                />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-forest-950/20 to-transparent"></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-forest-950 mb-6">
            Not Sure Which Service You Need?
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            Contact us for a free consultation. We'll assess your property and recommend the perfect services for your lawn.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-6 text-lg"
                data-testid="services-quote-btn"
              >
                Schedule a Free Consultation
              </Button>
            </Link>
            <a href="tel:+1-771-1871-987">
              <Button 
                variant="outline" 
                className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-full px-8 py-6 text-lg"
                data-testid="services-call-btn"
              >
                <Phone className="mr-2 w-5 h-5" />
                +1-771-1871-987
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
